import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTrip, getTripMeta } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Hash, CaretRight, CalendarBlank, UsersThree, MapPin, Trash, Copy, CheckCircle, ShieldCheck, ArrowClockwise, WifiSlash, Flag, ListChecks, Tag } from '@phosphor-icons/react';
import InstallPrompt from '../components/InstallPrompt';
import OwnerPasswordDialog from '../components/OwnerPasswordDialog';
import { getAccessedCodes, addAccessedCode, removeAccessedCode } from '../lib/tripHistory';
import { useAuth } from '../lib/authContext';
import { computeEventStatus } from '../lib/eventStatus';
import api from '../lib/api';

const HERO_IMG = 'https://images.unsplash.com/photo-1728461858751-c0c3dc0330ef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200';
const EVENT_LABELS = { trip: 'Golf Trip', society: 'Society Day', casual: 'Casual Round' };
const EVENT_COLORS = { trip: 'bg-[#1B4332] text-white', society: 'bg-[#5C6BC0] text-white', casual: 'bg-[#E59000] text-white' };

export default function Home() {
  const navigate = useNavigate();
  const { organiser, loading: authLoading } = useAuth();
  const [selfServiceEnabled, setSelfServiceEnabled] = useState(false);
  const [trips, setTrips] = useState(() => {
    try { const c = sessionStorage.getItem('foretour:home:trips'); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const [loading, setLoading] = useState(trips.length === 0);
  const [fetchError, setFetchError] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [allCodes, setAllCodes] = useState(() => getAccessedCodes());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [newEventAuthOpen, setNewEventAuthOpen] = useState(false);

  useEffect(() => {
    api.get('/settings/public').then(r => setSelfServiceEnabled(r.data.self_service_enabled)).catch(() => {});
  }, []);

  const loadTrips = useCallback(async () => {
    setFetchError(false);
    setLoading(true);
    const codes = getAccessedCodes();
    if (!codes.length) { setLoading(false); return; }
    try {
      const { data } = await api.post('/batch/trip-summaries', { codes });
      const summaries = data.trips || [];
      // Remove codes for trips that no longer exist
      const foundCodes = new Set(summaries.map(t => t.code));
      // Only remove if the server responded (we got data) — don't remove on empty response
      if (summaries.length > 0 || codes.length <= 3) {
        codes.filter(c => !foundCodes.has(c.toUpperCase())).forEach(c => removeAccessedCode(c));
        setAllCodes(getAccessedCodes());
      }
      const sorted = summaries.sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      setTrips(sorted);
      try { sessionStorage.setItem('foretour:home:trips', JSON.stringify(sorted)); } catch {}
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTrips(); }, [loadTrips]);

  const handleJoin = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setJoinLoading(true);
    setJoinError('');
    try {
      const { data } = await getTripMeta(code);
      if (!data.exists) throw new Error('not found');
      addAccessedCode(code);
      setAllCodes(getAccessedCodes());
      setJoinOpen(false);
      navigate(`/trip/${code}`);
    } catch {
      setJoinError('Event not found. Check the code and try again.');
    } finally { setJoinLoading(false); }
  };

  const handleRemoveTrip = (code, e) => {
    e.stopPropagation();
    setDeleteTarget({ code, name: (trips.find(t => t.code === code) || {}).name || code });
  };

  const handleConfirmRemove = () => {
    if (!deleteTarget) return;
    removeAccessedCode(deleteTarget.code);
    setAllCodes(getAccessedCodes());
    setTrips(prev => prev.filter(t => t.code !== deleteTarget.code));
    setDeleteTarget(null);
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF]" data-testid="home-screen">
      {/* Hero */}
      <div className="relative h-52 sm:h-60 overflow-hidden">
        <img src={HERO_IMG} alt="Golf course" className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-scrim" />
        {/* Organiser nav — hidden during initial auth check to prevent flash */}
        {!authLoading && (selfServiceEnabled || organiser || sessionStorage.getItem('foretour_platform_token')) && (
          <div className="absolute top-3 right-4 z-10 flex items-center gap-2">
            {organiser ? (
              <>
                <button onClick={() => navigate('/account')} className="text-xs text-[#1B4332] font-semibold hover:brightness-110 bg-[#C9A24A] rounded-lg px-3 py-1.5" data-testid="nav-account">Account</button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="text-xs text-[#1B4332] font-semibold hover:brightness-110 bg-[#C9A24A] rounded-lg px-3 py-1.5" data-testid="nav-login">Log in</button>
                <button onClick={() => navigate('/signup')} className="text-xs text-[#1B4332] font-semibold bg-[#C9A24A] rounded-lg px-3 py-1.5 hover:brightness-110" data-testid="nav-signup">Sign up</button>
              </>
            )}
          </div>
        )}
        <div className="absolute inset-0 flex flex-col justify-end p-5 pb-6">
          <img src="/branding/foretour-lockup-reversed.svg" alt="Fore Tour" loading="eager" className="w-[70%] max-w-[280px] h-auto" />
          <p className="text-[#E8E5DB] text-sm mt-1 font-medium">Your events</p>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 -mt-5 relative z-10 flex gap-3 max-w-lg mx-auto">
        <Button data-testid="create-event-btn" onClick={() => {
            if (organiser) {
              navigate('/wizard/new?step=type');
            } else if (selfServiceEnabled || localStorage.getItem('foretour:platformToken')) {
              setNewEventAuthOpen(true);
            } else {
              setOwnerDialogOpen(true);
            }
          }}
          className="flex-1 h-14 bg-[#1B4332] hover:bg-[#163B2B] text-white shadow-md rounded-xl text-base font-semibold">
          <Plus className="w-5 h-5 mr-2" weight="bold" /> New Event
        </Button>
        <Button data-testid="join-event-btn" onClick={() => setJoinOpen(true)} variant="outline"
          className="flex-1 h-14 bg-white border-[#E1E6E4] text-[#1D242B] hover:bg-[#E8E5DB] shadow-md rounded-xl text-base font-semibold">
          <Hash className="w-5 h-5 mr-2" weight="bold" /> Join Code
        </Button>
      </div>

      {/* Trip list */}
      <div className="px-4 pt-6 pb-24 max-w-lg mx-auto">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />)}</div>
        ) : fetchError ? (
          <div className="text-center py-16 animate-fade-in-up" data-testid="dashboard-error-state">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFF3E0] flex items-center justify-center">
              <WifiSlash className="w-7 h-7 text-[#E59000]" weight="fill" />
            </div>
            <p className="text-[#2C3742] text-base font-semibold">Couldn't load your events</p>
            <p className="text-[#455561] text-sm mt-1">Check your connection and try again.</p>
            <Button onClick={loadTrips} aria-label="Retry loading events"
              className="mt-5 h-11 px-6 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl text-sm font-semibold" data-testid="dashboard-retry-btn">
              <ArrowClockwise className="w-4 h-4 mr-2" weight="bold" /> Retry
            </Button>
            <p className="text-xs text-[#B8C7C0] mt-4">Still not working? Contact the event organiser.</p>
          </div>
        ) : trips.length === 0 ? (
          <div className="text-center py-20 animate-fade-in-up">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E8E5DB] flex items-center justify-center">
              <MapPin className="w-7 h-7 text-[#1B4332]" weight="fill" />
            </div>
            <p className="text-[#2C3742] text-base font-semibold">No events on this device yet.</p>
            <p className="text-[#455561] text-sm mt-1">Events you've set up are in your <button onClick={() => navigate(organiser ? '/account' : '/login')} className="text-[#1B4332] font-semibold underline">Account</button>: tap <strong>Open</strong> to add them here. Or join an event with its code.</p>
          </div>
        ) : (
          <div className="space-y-3 stagger-children">
            {trips.map(trip => (
              <Card key={trip.code} data-testid={`trip-card-${trip.code}`}
                className="p-4 cursor-pointer hover:shadow-md transition-all border-[#E1E6E4] bg-white group rounded-xl"
                onClick={() => navigate(`/trip/${trip.code}`)}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Badge className={`text-xs px-2 py-0.5 rounded-md font-semibold ${EVENT_COLORS[trip.event_type] || EVENT_COLORS.trip}`}>
                        {EVENT_LABELS[trip.event_type] || 'Golf Trip'}
                      </Badge>
                      {(() => { const s = computeEventStatus(trip); return <Badge className={`text-[9px] px-1.5 py-0.5 rounded-md font-semibold ${s.color}`}>{s.label}</Badge>; })()}
                      <span className="text-xs text-[#455561] font-mono">{trip.code}</span>
                    </div>
                    <h3 className="font-bold text-[#1D242B] text-lg truncate">{trip.name}</h3>
                    {(trip.start_date || trip.end_date) && (
                      <p className="text-xs text-[#455561] mt-0.5">{trip.start_date}{trip.start_date && trip.end_date ? ' — ' : ''}{trip.end_date}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-[#455561]">
                      <span className="flex items-center gap-1"><UsersThree className="w-4 h-4" weight="fill" /> {trip.player_count ?? trip.players?.length ?? 0}</span>
                      <span className="flex items-center gap-1"><CalendarBlank className="w-4 h-4" /> {trip.round_count ?? trip.rounds?.length ?? 0} round{(trip.round_count ?? trip.rounds?.length ?? 0)!==1?'s':''}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <button data-testid={`remove-trip-${trip.code}`} onClick={(e) => handleRemoveTrip(trip.code, e)}
                      className="p-2 text-[#B8C7C0] hover:text-[#D32F2F] transition-colors opacity-0 group-hover:opacity-100">
                      <Trash className="w-4 h-4" weight="bold" />
                    </button>
                    <CaretRight className="w-5 h-5 text-[#B8C7C0]" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Install prompt */}
      <div className="px-0 max-w-lg mx-auto">
        <InstallPrompt />
      </div>

      {/* My event codes */}
      {allCodes.length > 0 && (
        <div className="px-4 mt-4 max-w-lg mx-auto" data-testid="trip-codes-section">
          <p className="text-xs font-semibold text-[#B8C7C0] uppercase tracking-wider mb-2">My event codes</p>
          <Card className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden divide-y divide-[#F4F6F5]">
            {allCodes.map(code => (
              <div key={code} className="flex items-center gap-3 px-4 min-h-[48px]" data-testid={`trip-code-row-${code}`}>
                <span className="font-mono text-sm font-bold text-[#1D242B] tracking-widest flex-1">{code}</span>
                <button onClick={() => {
                  navigator.clipboard?.writeText(code);
                  setCopiedCode(code);
                  setTimeout(() => setCopiedCode(null), 1500);
                }} className="p-2 text-[#455561] hover:text-[#1B4332] transition-colors" data-testid={`copy-code-${code}`}>
                  {copiedCode === code ? <CheckCircle className="w-4 h-4 text-[#1B4332]" weight="fill" /> : <Copy className="w-4 h-4" weight="bold" />}
                </button>
                <button onClick={(e) => handleRemoveTrip(code, e)} className="p-2 text-[#B8C7C0] hover:text-[#D32F2F] transition-colors" data-testid={`remove-code-${code}`}>
                  <Trash className="w-4 h-4" weight="bold" />
                </button>
              </div>
            ))}
          </Card>
          <p className="text-xs text-[#455561] mt-2 px-1">Save these codes — clearing browser data will remove them.</p>
        </div>
      )}

      {/* Admin — visible only with active owner unlock */}
      {sessionStorage.getItem('foretour_platform_token') && (
      <div className="px-4 mt-8 mb-6 max-w-lg mx-auto space-y-2">
        <button onClick={() => navigate('/admin/approve-capacity')}
          className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border border-[#E1E6E4] bg-white text-[#1D242B] text-sm font-semibold hover:bg-[#F4F6F5] transition-colors"
          data-testid="approve-capacity-link">
          <ShieldCheck className="w-5 h-5 text-[#E59000]" weight="fill" />
          <span>Approve Capacity</span>
        </button>
        <button onClick={() => navigate('/admin/courses')}
          className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border border-[#E1E6E4] bg-white text-[#1D242B] text-sm font-semibold hover:bg-[#F4F6F5] transition-colors"
          data-testid="courses-link">
          <Flag className="w-5 h-5 text-[#1B4332]" weight="fill" />
          <span>Courses</span>
        </button>
        <button onClick={() => navigate('/admin/course-requests')}
          className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border border-[#E1E6E4] bg-white text-[#1D242B] text-sm font-semibold hover:bg-[#F4F6F5] transition-colors"
          data-testid="course-requests-link">
          <ListChecks className="w-5 h-5 text-[#1565C0]" weight="fill" />
          <span>Course Requests</span>
        </button>
        <button onClick={() => navigate('/admin/promo-codes')}
          className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border border-[#E1E6E4] bg-white text-[#1D242B] text-sm font-semibold hover:bg-[#F4F6F5] transition-colors"
          data-testid="promo-codes-link">
          <Tag className="w-5 h-5 text-[#1B4332]" weight="fill" />
          <span>Promo Codes</span>
        </button>
      </div>
      )}

      {/* Join Dialog */}
      <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
        <DialogContent className="sm:max-w-sm rounded-2xl" aria-describedby="join-dialog-desc">
          <DialogHeader><DialogTitle>Join Event</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2" id="join-dialog-desc">
            <Input data-testid="join-code-input" placeholder="Enter event code" value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              className="text-center text-lg font-mono tracking-widest h-14 rounded-xl" maxLength={8} autoFocus />
            {joinError && <p className="text-sm text-[#D32F2F] text-center" data-testid="join-error">{joinError}</p>}
          </div>
          <DialogFooter>
            <Button data-testid="join-submit-btn" onClick={handleJoin} disabled={joinLoading || !joinCode.trim()}
              className="w-full bg-[#1B4332] hover:bg-[#163B2B] text-white h-14 rounded-xl text-base font-semibold">
              {joinLoading ? 'Looking up...' : 'Join'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation — PIN + confirm code required */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle>Remove from this device?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#455561] py-1">The event isn't deleted and stays in your account.</p>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={handleConfirmRemove}
              className="flex-1 bg-[#D32F2F] hover:bg-red-700 text-white rounded-xl" data-testid="confirm-remove-btn">
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <OwnerPasswordDialog open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen}
        onSuccess={(token) => { setOwnerDialogOpen(false); sessionStorage.setItem('foretour_platform_token', token); navigate('/create', { state: { ownerToken: token } }); }}
        description="Enter the owner password to create a new event." />
      {/* New event auth dialog — shown when not logged in and self-service enabled */}
      <Dialog open={newEventAuthOpen} onOpenChange={setNewEventAuthOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create your Fore Tour account to set up your event</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Button onClick={() => { setNewEventAuthOpen(false); navigate('/signup'); }}
              className="w-full h-12 bg-[#1B4332] text-white rounded-xl font-semibold" data-testid="auth-signup">Sign up</Button>
            <Button onClick={() => { setNewEventAuthOpen(false); navigate('/login'); }}
              variant="outline" className="w-full h-12 border-[#E1E6E4] rounded-xl font-semibold" data-testid="auth-login">Log in</Button>
            <Button onClick={() => { setNewEventAuthOpen(false); setOwnerDialogOpen(true); }}
              variant="ghost" className="w-full h-10 text-sm text-[#455561]" data-testid="auth-owner">I have an owner password</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
